import React, {
  useCallback, useContext, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import {
  AssetDistributionOption, ButtonBar, ScreenHeader
} from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAAlternateAgent() {
  const { powerOfAttorney, setPowerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  
  useScrollToTop();
  
  const [ wantsAlternate, setWantsAlternate ] = useState(powerOfAttorney.alternate_agents?.length > 0);
  const [ alternateAgent, setAlternateAgent ] = useState(
    powerOfAttorney.alternate_agents?.[0] || {
      name: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      alt_phone: '',
      email: ''
    }
  );

  const handleAlternateChange = (field: string, value: string) => {
    setAlternateAgent(prev => ({ ...prev, [field]: value }));
  };

  const handleBack = useCallback(() => {
    navigate('../agent-details');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    const alternateAgents = wantsAlternate ? [ alternateAgent ] : [];
    setPowerOfAttorney(prev => ({ 
      ...prev, 
      alternate_agents: alternateAgents
    }));
    navigate('../powers-intro');
  }, [ wantsAlternate, alternateAgent, setPowerOfAttorney, navigate ]);

  const isFormValid = !wantsAlternate || (
    alternateAgent.name && 
    alternateAgent.address && 
    alternateAgent.city && 
    alternateAgent.state && 
    alternateAgent.zip && 
    alternateAgent.phone
  );
  
  return (
    <div className="new-poa-alternate-agent screen-container screen">
      <ScreenHeader
        title="Alternate Agent"
        description="You can name an alternate agent who will step in if your primary agents become unavailable"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <h3>Do You Want to Name an Alternate Agent?</h3>
        <p>
          An alternate agent will only act if your primary agents become unavailable, 
          unwilling, or unable to serve. This provides an extra layer of protection 
          for your financial affairs.
        </p>

        <div className="asset-distribution-option-container">
          <AssetDistributionOption
            id="name-alternate"
            label="Name an Alternate Agent"
            description="I want to name someone who can step in if needed"
            value="1"
          />
          <AssetDistributionOption
            id="no-alternate"
            label="No Alternate Agent"
            description="I don't want to name an alternate agent at this time"
            value="0"
          />
        </div>

        {wantsAlternate && (
          <div className="alternate-agent-form">
            <h3>Alternate Agent Details</h3>
            
            <div className="form-group">
              <label htmlFor="alt-agent-name" className="form-label">
                Full Name *
              </label>
              <input
                id="alt-agent-name"
                type="text"
                value={alternateAgent.name}
                onChange={(e) => handleAlternateChange('name', e.target.value)}
                className="form-input"
                placeholder="Enter alternate agent's full name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="alt-agent-address" className="form-label">
                Street Address *
              </label>
              <input
                id="alt-agent-address"
                type="text"
                value={alternateAgent.address}
                onChange={(e) => handleAlternateChange('address', e.target.value)}
                className="form-input"
                placeholder="Enter alternate agent's street address"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="alt-agent-city" className="form-label">
                  City *
                </label>
                <input
                  id="alt-agent-city"
                  type="text"
                  value={alternateAgent.city}
                  onChange={(e) => handleAlternateChange('city', e.target.value)}
                  className="form-input"
                  placeholder="City"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="alt-agent-state" className="form-label">
                  State *
                </label>
                <input
                  id="alt-agent-state"
                  type="text"
                  value={alternateAgent.state}
                  onChange={(e) => handleAlternateChange('state', e.target.value)}
                  className="form-input"
                  placeholder="State"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="alt-agent-zip" className="form-label">
                  ZIP Code *
                </label>
                <input
                  id="alt-agent-zip"
                  type="text"
                  value={alternateAgent.zip}
                  onChange={(e) => handleAlternateChange('zip', e.target.value)}
                  className="form-input"
                  placeholder="ZIP"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="alt-agent-phone" className="form-label">
                  Phone Number *
                </label>
                <input
                  id="alt-agent-phone"
                  type="tel"
                  value={alternateAgent.phone}
                  onChange={(e) => handleAlternateChange('phone', e.target.value)}
                  className="form-input"
                  placeholder="(555) 123-4567"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="alt-agent-alt-phone" className="form-label">
                  Alternate Phone
                </label>
                <input
                  id="alt-agent-alt-phone"
                  type="tel"
                  value={alternateAgent.alt_phone}
                  onChange={(e) => handleAlternateChange('alt_phone', e.target.value)}
                  className="form-input"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="alt-agent-email" className="form-label">
                Email Address (Optional)
              </label>
              <input
                id="alt-agent-email"
                type="email"
                value={alternateAgent.email}
                onChange={(e) => handleAlternateChange('email', e.target.value)}
                className="form-input"
                placeholder="agent@example.com"
              />
            </div>
          </div>
        )}
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue"
        disablePrimary={!isFormValid}
      />
    </div>
  );
}
